import { Button } from '@repo/ui/button'
import {
	Combobox,
	ComboboxChip,
	ComboboxChips,
	ComboboxChipsInput,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxItem,
	ComboboxList,
	ComboboxValue,
	useComboboxAnchor
} from '@repo/ui/combobox'
import { useQuery } from '@tanstack/react-query'
import { MapPin } from 'lucide-react'
import { useTranslation } from '@/lib/i18n'
import { institutionsQueryOptions } from '@/query/query-options'

interface Option {
	code: string
	label: string
	/** The university has a site in the city of the residence. */
	nearby: boolean
}

/**
 * The universities whose students may apply, searched by name in the
 * university catalogue and kept as chips. Those with a site in the city of
 * the residence come first. A university the residence admits that the
 * catalogue no longer lists stays as a chip, and says so, until removed.
 */
function InstitutionPicker({
	id,
	value,
	onChange,
	city,
	invalid,
	disabled
}: {
	id: string
	value: string[]
	onChange: (value: string[]) => void
	/** The city of the residence, to offer its universities first. */
	city: string
	invalid?: boolean
	disabled?: boolean
}) {
	const { t } = useTranslation()
	const anchor = useComboboxAnchor()
	const institutions = useQuery(institutionsQueryOptions())

	if (institutions.isLoading) {
		return (
			<p className='text-muted-foreground text-sm'>
				{t('admin-residences:institutions.loading')}
			</p>
		)
	}
	if (institutions.isError) {
		return (
			<div className='flex items-center gap-3 text-sm'>
				<p className='text-destructive'>
					{t('admin-residences:institutions.load_failed')}
				</p>
				<Button
					onClick={() => institutions.refetch()}
					size='sm'
					type='button'
					variant='outline'
				>
					{t('admin-residences:institutions.retry')}
				</Button>
			</div>
		)
	}

	const listed = institutions.data ?? []
	if (listed.length === 0) {
		return (
			<p className='text-muted-foreground text-sm'>
				{t('admin-residences:institutions.empty')}
			</p>
		)
	}
	const place = city.trim().toLocaleLowerCase('it')
	const listedCodes = new Set(listed.map((item) => item.code))
	const options: Option[] = [
		...listed
			.map((item) => ({
				code: item.code,
				label: item.name,
				nearby:
					place !== '' &&
					item.sites.some(
						(site) =>
							site.municipalityName.toLocaleLowerCase('it') ===
							place
					)
			}))
			// Stable: the catalogue order holds within each group.
			.sort((a, b) => Number(b.nearby) - Number(a.nearby)),
		...value
			.filter((code) => !listedCodes.has(code))
			.map((code) => ({
				code,
				label: t('admin-residences:institutions.not_listed', { code }),
				nearby: false
			}))
	]
	const selected = options.filter((option) => value.includes(option.code))

	return (
		<Combobox
			disabled={disabled}
			isItemEqualToValue={(item, picked) => item.code === picked.code}
			items={options}
			itemToStringLabel={(item) => item.label}
			multiple
			onValueChange={(next, details) => {
				// Escape on the closed list clears every chip in Base UI: here it
				// only closes the list, the universities chosen stay.
				if (details.reason === 'escape-key') {
					return
				}
				onChange(next.map((item) => item.code))
			}}
			value={selected}
		>
			<ComboboxChips aria-invalid={invalid} ref={anchor}>
				<ComboboxValue>
					{(items: Option[]) =>
						items.map((item) => (
							<ComboboxChip
								className='bg-primary text-primary-foreground'
								key={item.code}
							>
								{item.label}
							</ComboboxChip>
						))
					}
				</ComboboxValue>
				<ComboboxChipsInput
					aria-invalid={invalid}
					id={id}
					placeholder={
						selected.length === 0
							? t('admin-residences:institutions.search')
							: undefined
					}
				/>
			</ComboboxChips>
			<ComboboxContent anchor={anchor}>
				<ComboboxEmpty>
					{t('admin-residences:institutions.no_match')}
				</ComboboxEmpty>
				<ComboboxList>
					{(item: Option) => (
						<ComboboxItem key={item.code} value={item}>
							<span className='min-w-0 flex-1'>{item.label}</span>
							{item.nearby && (
								<span className='flex shrink-0 items-center gap-1 text-muted-foreground text-xs'>
									<MapPin aria-hidden className='size-3' />
									{city.trim()}
								</span>
							)}
						</ComboboxItem>
					)}
				</ComboboxList>
			</ComboboxContent>
		</Combobox>
	)
}

export { InstitutionPicker }
